import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1095CInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 1095-C instructions for Tax Year 2025"
          description="Learn IRS Form 1095-C step-by-step instructions for Tax Year 2025. Accurately report employer-provided health coverage and stay compliant with TaxZerone."
          keywords=""
          canonical="resources/information-returns/form-1095-c-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,

            mt: 3,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight"> 1095-C/1094-C</span>
          </span>{' '}
          Instructions
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Learn about Form 1095-C/1094-C and file using the instructions
          provided
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1095cinstructions`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-1095-c-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1095-C at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Form 1095-C</strong>
                        {
                          ' reports information about employer-provided health insurance coverage offered by applicable large employers (ALEs) under the Affordable Care Act (ACA).'
                        }
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        {
                          'Employers use this form to report whether minimum essential coverage was offered to employees and provide details needed to '
                        }
                        determine
                        {' eligibility for premium tax credits.'}
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Employee copies
                        </strong>
                        {' must '}
                        generally be furnished
                        {' by '}
                        <strong style={{ fontWeight: 600 }}>February 28</strong>
                        {' following the calendar year being reported. The '}
                        <strong style={{ fontWeight: 600 }}>
                          IRS filing deadline
                        </strong>
                        {' is '}
                        <strong style={{ fontWeight: 600 }}>February 28</strong>
                        {' for paper filings and '}
                        <strong style={{ fontWeight: 600 }}>March 31</strong>
                        {' for electronic filings.'}
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        {'This article covers '}
                        <strong style={{ fontWeight: 600 }}>
                          line-by-line instructions
                        </strong>
                        {', including employer and employee details, '}
                        <strong style={{ fontWeight: 600 }}>{'Part I '}</strong>
                        (Employee and Applicable Large Employer Member
                        Information)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>
                          {'Part II '}
                        </strong>
                        (Employee Offer and Coverage Information)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>
                          Line 14 (Offer of Coverage), Line 15 (Employee Share
                          of Lowest Cost Monthly Premium), Line 16 (Applicable
                          Section 4980H Safe Harbor or Other Relief), Line 17
                          (Zip Code)
                        </strong>
                        {', and '}
                        <strong style={{ fontWeight: 600 }}>
                          Part III (Covered Individuals Information)
                        </strong>
                        {', including '}
                        <strong style={{ fontWeight: 600 }}>
                          Covered Individual Name, Social Security Number or
                          Date of Birth, and Months of Coverage
                        </strong>
                        .
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Penalties:</strong>
                        {' Late, incorrect, or incomplete filing of '}
                        <strong style={{ fontWeight: 600 }}>Form 1095-C</strong>
                        {' may result in IRS penalties ranging from '}
                        <strong style={{ fontWeight: 600 }}>
                          $60 to $680 per return
                        </strong>
                        , depending on the delay, correction requirements, and
                        whether the employer meets electronic filing
                        obligations.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="6" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Extensions:</strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-8809-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          <strong style={{ fontWeight: 600 }}>
                            File Form 8809
                          </strong>
                        </Link>
                        {' to request an automatic filing extension. '}
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          <strong style={{ fontWeight: 600 }}>
                            File Form 15397
                          </strong>
                        </Link>{' '}
                        {'to request the full '}
                        <strong style={{ fontWeight: 600 }}>
                          30-day extension
                        </strong>
                        {' for furnishing recipient copies'}
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  All Applicable Large Employers should report the health
                  coverage offered by the employer to the employee and the
                  enrollment status of employees in the health coverage. This
                  report should be filed to the IRS using forms 1095-C and
                  1094-C and send the employee copy of form 1095-C to the
                  respective employees.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Applicable Large Employer
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  An Applicable Large Employer (ALE) is an organization that is
                  legally required to provide health coverage to its employees
                  under the Affordable Care Act (ACA). A company or entity
                  qualifies as an ALE if it has at least 50 full-time employees,
                  including full-time equivalent employees, during the previous
                  calendar year. This classification determines the employer’s
                  responsibilities regarding health insurance coverage and
                  reporting requirements.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Full-time Equivalent Employees
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  An employee who works fewer than 120 hours in a month is not
                  classified as a full-time employee. However, their hours
                  contribute to the calculation of Full-Time Equivalent (FTE)
                  employees for the entity. To determine the number of FTE
                  employees, the total hours worked by non-full-time employees
                  in a month is divided by 120.
                </Typography>
                <Alert
                  severity="info"
                  icon={false}
                  sx={{
                    // mt: 0,
                    // ml: { sm: 5, xs: 1 },
                    // mr: { sm: 8, xs: 1 },
                    mb: 4,
                    textAlign: 'left',
                    borderLeft: '3px solid #0288d1',
                  }}
                >
                  <b> Example:</b> Suppose a company has 45 full-time employees
                  and 30 part-time employees who collectively work 720 hours in
                  a month. The calculation for FTE employees would be:
                  <br style={{ marginBottom: 8 }} />
                  FTE Employees = 720 ÷ 120 = 6{' '}
                  <br style={{ marginBottom: 8 }} />
                  In this case, the entity has 45 full-time employees and 6 FTE
                  employees, totaling 51 employees. Since the total exceeds 50,
                  the company qualifies as an Applicable Large Employer
                  (ALE).{' '}
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who needs to File Form 1095-C?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  All Applicable Large Employers (ALEs) are required to{' '}
                  <Link
                    href="/aca-forms/e-file-form-1095c/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 1095-C
                  </Link>{' '}
                  along with Form 1094-C as part of their reporting obligations
                  under the Affordable Care Act (ACA). Form 1095-C must be
                  provided to each eligible employee, as it serves as a crucial
                  document for individuals when filing their federal tax
                  returns. Employees need this form to determine their
                  eligibility for the Premium Tax Credit while completing Form
                  1040. Additionally, the IRS uses the information on these
                  forms to verify employer compliance with ACA regulations.{' '}
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Important dates for filing
                </Typography>
                <Box component="ul" sx={{ mb: 1.5, '& li': { mb: 1 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      File with IRS (paper)
                    </span>{' '}
                    <br />
                    Employers filing less than 10 information returns for a tax
                    year can file form 1095-C/1094-C using the paper filing
                    method <br style={{ marginBottom: 8 }} />
                    <Box className="deadline-dates" sx={{}}>
                      <Typography
                        variant="body1"
                        sx={{
                          p: 2,
                          color: '#fff',
                          fontSize: 18,
                          fontWeight: 500,
                          mt: 0,
                        }}
                      >
                        <b style={{ fontSize: 22 }}>
                          {' '}
                          Deadline for the Tax year 2025 :
                        </b>{' '}
                        March 02, 2026
                      </Typography>
                    </Box>
                  </li>

                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      File with the IRS (Electronic Filing)
                    </span>{' '}
                    <br />
                    Employers are advised to file forms 1095-C and 1094-C using
                    electronic filing (E-Filing) for easy filing and faster
                    processing <br style={{ marginBottom: 8 }} />
                    <Box className="deadline-dates" sx={{}}>
                      <Typography
                        variant="body1"
                        sx={{
                          p: 2,
                          color: '#fff',
                          fontSize: 18,
                          fontWeight: 500,
                          mt: 0,
                        }}
                      >
                        <b style={{ fontSize: 22 }}>
                          {' '}
                          Deadline for the Tax year 2025 :
                        </b>{' '}
                        March 31, 2026
                      </Typography>
                    </Box>
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Send Recipient copies
                    </span>{' '}
                    <br />
                    The employer must provide form 1095-C electronically or
                    through paper to the respective employee before the deadline
                    to stay compliant with IRS regulation{' '}
                    <br style={{ marginBottom: 8 }} />
                    <Box className="deadline-dates" sx={{}}>
                      <Typography
                        variant="body1"
                        sx={{
                          p: 2,
                          color: '#fff',
                          fontSize: 18,
                          fontWeight: 500,
                          mt: 0,
                        }}
                      >
                        <b style={{ fontSize: 22 }}>
                          {' '}
                          Deadline for the Tax year 2025 :
                        </b>{' '}
                        March 2, 2026
                      </Typography>
                    </Box>
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{
                    // mt: 0,
                    // ml: { sm: 5, xs: 1 },
                    // mr: { sm: 8, xs: 1 },
                    mb: 4,
                    textAlign: 'left',
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <b>Note:</b> If the deadline is on a weekend or holiday, the
                  next working day will be considered as the deadline.
                </Alert>
                <Box
                  component="div"
                  sx={{ mb: 2, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Taxzerone encourages ALE to submit their forms using
                        electronic filing methods and send recipient copies
                        using ZeroneVault for secure processing.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 110, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1095cinstructions`}
                      >
                        File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 1095-C Penalty:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the employer fails to file Form 1095-C within the deadline,
                  the IRS will impose a penalty for failure to file and failure
                  to provide correct information to the employee. The penalty
                  starts at $340 for each return and has an upper limit of
                  $4,098,500 for a calendar year.{' '}
                </Typography>
                <Box
                  component="div"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                  }}
                >
                  <Stack
                    direction={{ md: 'row', sm: 'row', xs: 'column' }}
                    sx={{
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Box sx={{ width: 900, maxWidth: '100%' }}>
                      {/* <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          fontSize: '24px !important',
                        }}
                      >
                        Why Risk penalties?
                      </Typography> */}
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          fontSize: { lg: 17, md: 18, sm: 16 },
                        }}
                      >
                        Avoid the last-minute rush and penalty. Start your Form
                        1095-C Electronic filing with us!
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: 250,
                        maxWidth: '100%',
                        textAlign: { xs: 'center', sm: 'right' },
                        mt: 2,
                      }}
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1094cinstructions`}
                      >
                        File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1.5,
                  }}
                >
                  Instructions to file Form 1095-C along with 1094-C
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Form 1094-C: Transmittal of Employer-Provided Health Insurance
                  Offer and Coverage Information Return
                </Typography>{' '}
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                  }}
                >
                  Part I - Applicable Large Employer Member (ALE Member){' '}
                </Typography>
                <Box sx={{ border: '1px solid #333', mb: 1 }}>
                  <picture>
                    <source srcSet="/assets/images/member-1094-c-img.webp" />
                    <source srcSet="/assets/images/member-1094-c-img.png" />
                    <img
                      src="/assets/images/member-1094-c-img.png"
                      loading="lazy"
                      alt="Part I - Applicable Large Employer Member (ALE Member)"
                      width={749}
                      height={335}
                      className="proportional-img"
                    />
                  </picture>
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> Line 1:</span> Enter the
                  name of the Applicable Large Employer.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  <span style={{ fontWeight: 600 }}> Line 2:</span> Enter the
                  nine-digit Employer Identification Number (EIN) of the
                  Applicable Large Employer. SSN should not be entered and EIN
                  should be entered along with the dash
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Line 3 through Line 6:
                  </span>{' '}
                  Enter the street address, city, state, country, and Zip code
                  of the employer. Verify that this address matches the ALE
                  member address on form 1095-C.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  <span style={{ fontWeight: 600 }}> Line 7:</span> Enter the
                  responsible person&apos;s name for responding to queries from
                  the IRS regarding to filing of forms 1094-C and 1095-C.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  <span style={{ fontWeight: 600 }}> Line 8:</span> Enter the
                  Responsible Person&apos;s phone number for answering IRS
                  queries
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{
                    // mt: 0,
                    // ml: { sm: 5, xs: 1 },
                    // mr: { sm: 8, xs: 1 },
                    mb: 3,
                    textAlign: 'left',
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <b>Note:</b> Fill out lines 9–16 if you are a Designated
                  Governmental Entity (DGE) filing on behalf of an ALE Member.
                  Otherwise do not fill out lines 9–16. Go straight to line 18
                  instead.
                </Alert>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  <span style={{ fontWeight: 600 }}> Line 9:</span> Enter the
                  name of DGE, if a DGE is filing on behalf of the ALE member.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  <span style={{ fontWeight: 600 }}> Line 10:</span> Enter the
                  DGE’s Employer Identification Number (EIN).
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  <span style={{ fontWeight: 600 }}> Line 11 to 14:</span> Enter
                  the DGE’s street address, city, state, country, and zip code.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  <span style={{ fontWeight: 600 }}> Line 15 to 16:</span> Enter
                  the name and phone number of the person to contact who will
                  respond to any inquiries on your behalf to the IRS about the
                  information reported on or the filing of Form 1094-C.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  <span style={{ fontWeight: 600 }}> Line 17:</span> Reserved
                  for future use
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  <span style={{ fontWeight: 600 }}> Line 18:</span> Enter the
                  total number of Form 1095-C you plan to submit along with the
                  transmittal Form 1094-C.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}> Line 19:</span> Check the
                  box if this Form 1094-C transmittal is the Authoritative
                  Transmittal and complete Part II, Part III, and Part IV.
                  otherwise, leave this section blank and go to the signature
                  portion.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Part II - ALE Member Information:
                </Typography>
                <Box sx={{ border: '1px solid #333', mb: 1 }}>
                  <picture>
                    <source srcSet="/assets/images/ale-member-1094-c-img.webp" />
                    <source srcSet="/assets/images/ale-member-1094-c-img.png" />
                    <img
                      src="/assets/images/ale-member-1094-c-img.png"
                      loading="lazy"
                      alt="Part II - ALE Member Information:"
                      width={760}
                      height={189}
                      className="proportional-img"
                    />
                  </picture>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}> Line 20:</span> Enter the
                  total number of Forms 1095-C that will be submitted by or on
                  behalf of the ALE Member. This count should include all Forms
                  1095-C associated with this transmittal, covering individuals
                  enrolled in the employer-sponsored, self-insured plan (if
                  applicable), as well as any additional Forms 1095-C filed with
                  a separate transmittal by or for the ALE Member.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> Line 21:</span> Check the
                  “yes” box if you are an ALE member of an aggregated ALE Group
                  during any month of the calendar year.
                </Typography>
                <Box component="ul" sx={{ mb: 1.5, '& li': { mb: 1 } }}>
                  <li>
                    If you selected &quot;Yes,&quot; you should also fill out
                    column (d) of Part III, &quot;Aggregated Group
                    Indicator,&quot; and Part IV.{' '}
                  </li>
                  <li>
                    If you selected &quot;No&quot;, do not fill out column (d)
                    of Part III, &quot;Aggregated Group Indicator,&quot;, and
                    Part IV.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> Line 22:</span>
                </Typography>
                <Box component="ol" sx={{ mb: 3, '& li': { mb: 1 } }}>
                  <li>
                    Check the  €˜Qualifying Offer Method’ if the employer offers
                    the Qualifying medical coverage to the employee and their
                    dependent, but the employee didn’t take the offered
                    coverage.
                  </li>
                  <li>
                    Check the  €˜98% Offer Method’ if the employer offers
                    Minimum Essential Coverage for at least 98% of the
                    employees.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Part III - ALE Member Information—Monthly
                </Typography>
                <Box sx={{ border: '1px solid #333', mb: 2 }}>
                  <picture>
                    <source srcSet="/assets/images/information-1094-c-img.webp" />
                    <source srcSet="/assets/images/information-1094-c-img.png" />
                    <img
                      src="/assets/images/information-1094-c-img.png"
                      loading="lazy"
                      alt="Part III - ALE Member Information—Monthly "
                      width={757}
                      height={537}
                      className="proportional-img"
                    />
                  </picture>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 23 to 35:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Column(a)
                </Typography>
                <Box component="ul" sx={{ mb: 1.5, '& li': { mb: 1 } }}>
                  <li>
                    On line 23 Select “Yes” for “All 12 Months” if the ALE
                    Member provided minimum essential coverage, including an
                    individual coverage HRA, to at least 95% of its full-time
                    employees and their dependents for the complete calendar
                    year. If the employer failed to provide the mentioned
                    coverage Select “No” for “All 12 Months”.
                  </li>
                  <li>
                    For each applicable calendar month, Select the
                    &quot;Yes&quot; checkbox if the ALE Member-provided at least
                    95% of its full-time employees and their dependents with
                    minimum essential coverage.{' '}
                  </li>
                  <li>
                    For each applicable calendar month, Select the
                    &quot;No&quot; checkbox if the ALE Member failed to provide
                    at least 95% of its full-time employees and their dependents
                    with minimum essential coverage.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Column(b)
                </Typography>
                <Box component="ul" sx={{ mb: 1.5, '& li': { mb: 1 } }}>
                  <li>
                    Enter the total number of full-time working employees for
                    ALE members The total number of employees should not include
                    employees in the Limited Non-Assessment Period.
                    <Alert
                      severity="warning"
                      icon={false}
                      sx={{
                        // mt: 0,
                        // ml: { sm: 5, xs: 1 },
                        // mr: { sm: 8, xs: 1 },
                        // mb: { sm: 2, xs: 1 },
                        textAlign: 'left',
                        borderLeft: '3px solid rgb(102, 60, 0)',
                      }}
                    >
                      <b>Note:</b> If an ALE Member selected the 98% Offer
                      Method in part 2 line 22, column (b) is not necessary.
                    </Alert>
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Column(c)
                </Typography>
                <Box component="ul" sx={{ mb: 1.5, '& li': { mb: 1 } }}>
                  <li>
                    Enter the total number of working employees including
                    full-time employees, non-full-time employees, employees who
                    are in a Limited Non-Assessment Period,
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Column(d)
                </Typography>
                <Box component="ul" sx={{ mb: 1.5, '& li': { mb: 1 } }}>
                  <li>
                    Check the box in line 23 if the ALE was an aggregate member
                    for the entire 12 months, or select the specific
                    month&apos;s box from 24 to 35 during which the ALE was an
                    aggregate member.
                    <Alert
                      severity="warning"
                      icon={false}
                      sx={{
                        // mt: 0,
                        // ml: { sm: 5, xs: 1 },
                        // mr: { sm: 8, xs: 1 },
                        mb: 3,
                        textAlign: 'left',
                        borderLeft: '3px solid rgb(102, 60, 0)',
                      }}
                    >
                      <b>Note:</b> This information is required to be filled
                      only if you select  €˜Yes’ in line 21 of part 1
                    </Alert>
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Part IV - Other ALE Members of Aggregated ALE Group
                </Typography>
                <Box sx={{ border: '1px solid #333', mb: 1 }}>
                  <picture>
                    <source srcSet="/assets/images/aggregated-1094-c-img.webp" />
                    <source srcSet="/assets/images/aggregated-1094-c-img.png" />
                    <img
                      src="/assets/images/aggregated-1094-c-img.png"
                      loading="lazy"
                      alt="Part IV - Other ALE Members of Aggregated ALE Group "
                      width={760}
                      height={438}
                      className="proportional-img"
                    />
                  </picture>
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> Line 36 to 65:</span> Enter
                  the other ALE member&apos;s Names and Employer Identification
                  Numbers (EIN) of the aggregated ALE group during the calendar
                  year.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{
                    // mt: 0,
                    // ml: { sm: 5, xs: 1 },
                    // mr: { sm: 8, xs: 1 },
                    mb: 3,
                    textAlign: 'left',
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <b>Note:</b> This information is required to be filled only if
                  you select  €˜Yes’ in line 21 of part 1
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mt: 4,
                    mb: 2,
                  }}
                >
                  1095-C : Employer-Provided Health Insurance Coverage
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Part I - Employee and Applicable Large Employer Member
                  (Employer)
                </Typography>
                <Box sx={{ border: '1px solid #333', mb: 2 }}>
                  <picture>
                    <source srcSet="/assets/images/employer-1095-c-img.webp" />
                    <source srcSet="/assets/images/employer-1095-c-img.png" />
                    <img
                      src="/assets/images/employer-1095-c-img.png"
                      loading="lazy"
                      alt="Part I - Employee and Applicable Large Employer Member (Employer) "
                      width={750}
                      height={139}
                      className="proportional-img"
                    />
                  </picture>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 1:</span> Enter the
                  full name of the employee
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 2:</span> Enter the
                  Social Security Number (SSN) of the employee
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 3 through 6:</span>{' '}
                  Enter the street address, city, state, Country, and Zip code
                  of the employee in the appropriate field
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 7:</span> Enter the
                  legal full name of the employer (ALE member)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 8:</span> Enter the
                  Employer Identification Number (EIN) of the employer
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}> Line 9 through 13:</span>{' '}
                  Enter the street address, Phone number of the person the IRS
                  can contact to report the errors, city name, state, Country,
                  and Zip code of the employer in the given field.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Part II - Employee Offer of Coverage
                </Typography>
                <Box sx={{ border: '1px solid #333', mb: 1 }}>
                  <picture>
                    <source srcSet="/assets/images/coverage-1095-c-img.webp" />
                    <source srcSet="/assets/images/coverage-1095-c-img.png" />
                    <img
                      src="/assets/images/coverage-1095-c-img.png"
                      loading="lazy"
                      alt="Part II - Employee Offer of Coverage "
                      width={750}
                      height={163}
                      className="proportional-img"
                    />
                  </picture>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Employee’s Age on January 1:
                  </span>{' '}
                  Enter the Employee&apos;s Age on January 1, 2025, if the
                  employee was offered an individual coverage HRA.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Plan Start Month:</span>{' '}
                  Enter the 2-digit number of the calendar month when the plan
                  is started and from when the employee is offered health
                  coverage. If there is no health plan coverage offered to the
                  employee, Enter “00”.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 14:</span> Enter the
                  coverage offered by the employer to the employee, dependent on
                  the code format mentioned below. If the code is the same for
                  all 12 months only enter the code in  €˜All 12 months column’.
                  If the code is different for any of the months fill the code
                  for the respective month.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Code Format:{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> 1A -</span> It represents
                  the Minimum essential coverage(MEC) with the Minimum value(MV)
                  provided to you, your spouse, and dependents with an
                  employee-required contribution for self-only coverage equal to
                  or less than 9.5% (as adjusted) based on the single federal
                  poverty line.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> 1B -</span> It represents
                  the minimum essential coverage provided and the minimum value
                  offered only to employees.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> 1C -</span> It represents
                  the Minimum Essential Coverage provided Minimum value only to
                  employees and other dependents(Spouse is not included).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> 1D -</span> It represents
                  the Minimum Essential Coverage provided Minimum value only to
                  employees, and Spouse(Dependents are not included). Don’t use
                  this code if coverage is conditionally offered to Spouse,
                  instead use 1J
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> 1E -</span> It represents
                  the Minimum Essential Coverage provided Minimum value to their
                  employees, spouse, and dependents. Don’t use this code if
                  coverage is conditionally offered to the Spouse, instead use
                  1K
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> 1F -</span> It represents
                  the Minimum essential coverage NOT providing minimum value
                  offered to the employee, or employee and your spouse or
                  dependent(s), or employee, spouse, and dependent(s).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> 1G -</span> During one or
                  more months of the calendar year, you were enrolled in
                  self-insured employer-sponsored coverage, but you were not a
                  full-time employee during any of the months in the year.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mt: 2, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> 1G applies to  €˜all 12 months’ or not applicable
                  at all.
                </Alert>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> 1H -</span> No coverage
                  offered. This means either the employer has not offered any
                  coverage or the coverage offered does not meet the requirement
                  of Minimum Essential Coverage.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> 1I -</span> Reserved
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> 1J -</span> It represents
                  the Minimum essential coverage providing minimum value offered
                  to employees, Minimum value conditionally offered to your
                  spouse but not offered to employees dependent.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> 1K -</span> It stands for
                  the Minimum Essential Coverage that offers minimum value to
                  the employee, and the employee&apos;s dependents, and Minimum
                  Essential Coverage is conditionally offered to the
                  employee&apos;s spouse.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> 1L -</span> It stands for
                  the individual coverage health reimbursement arrangement
                  (HRA), which is only available to employees. Their
                  affordability is assessed using the ZIP code of the
                  employee&apos;s primary address.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> 1M -</span> This code
                  represents the Individual coverage HRA offered to employees
                  and their dependents but not offered to their spouse. Employee
                  affordability is determined by using the employee’s primary
                  residence ZIP code.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> 1N -</span> This code
                  represents the Individual coverage HRA offered to employee,
                  spouse, and dependents with affordability determined by using
                  the employee’s primary residence ZIP code.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> 1O -</span> This code
                  represents the Individual coverage HRA offered to employees
                  only using their primary employment site ZIP code
                  affordability safe harbor.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> 1P -</span> This code
                  represents the HRA provided to employees and their dependents
                  (but not their spouse) with individual coverage based on the
                  affordability safe harbor for the employee&apos;s primary
                  employment site ZIP code.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> 1Q -</span> It represents
                  the Individual coverage HRA offered to employees, spouse, and
                  dependents using the employee’s primary employment site ZIP
                  code affordability safe harbor
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> 1R -</span> This code
                  represents the Individual coverage HRA offered is not
                  affordable to employees; employees and spouse or dependents;
                  or employees, spouse, and dependents.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> 1S -</span> It represents
                  the Individual coverage HRA offered to an individual employee
                  who was not a full-time employee.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> 1T -</span> The Individual
                  coverage HRA offered to the employee and spouse (not to
                  dependents) with affordability determined using the employee’s
                  primary residence ZIP code.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> 1U -</span> The Individual
                  coverage HRA offered to the employee and spouse (not to their
                  dependents) using the employee’s primary employment site ZIP
                  code affordability safe harbor.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> 1V to 1Z -</span> Reserved
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 15:</span> Fill Line
                  15, only if you entered 1B, 1C, 1D, 1E, 1J, 1K, 1L, 1M, 1N,
                  1O, 1P, 1Q, 1T, or 1U code in Line 14. Enter the amount of the
                  Employee Required Contribution, which is the employee&apos;s
                  share of the monthly cost for the lowest-cost, self-only,
                  minimum essential coverage that is offered to the employee. If
                  the employee required contribution is Zero, enter 0.00
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 16:</span> Enter the
                  applicable  €˜2’ code series from the below. If the code is
                  the same for the month Enter the code in  €˜All 12 Months’. If
                  different codes are applicable for the different months then
                  fill in the code for the respective month. Leave the month
                  column blank if not applicable
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Code series{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> 2A- </span> Use code 2A, if
                  the employee was not worked on any day of the calendar month.
                  Don&apos;t use code 2A, if the employee worked on any day of
                  the ALE member or the employee terminates the employment with
                  the ALE member.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> 2B- </span> Use code 2B, if
                  the employee is not a full-time employee for the month and not
                  provided Minimum Essential coverage. Also, You can use Code
                  2B, if the employee was a full-time employee and their
                  coverage was terminated before the month&apos;s end due to
                  termination of employment.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> 2C- </span> Use code 2C on
                  line 16 if the employee was enrolled in the health coverage
                  offered by the ALE Member for every day of the month. However,
                  do not use code 2C in the following cases:
                </Typography>
                <Box component="ul" sx={{ mt: 2, mb: 1.5, '& li': { mb: 1 } }}>
                  <li>If code 1G is entered on line 14. </li>
                  <li>
                    If the multiemployer interim rule relief applies (use code
                    2E instead).{' '}
                  </li>
                  <li>
                    If the employee was terminated and enrolled in COBRA or
                    post-employment coverage (use code 2A instead).{' '}
                  </li>
                  <li>
                    If the coverage was not minimum essential coverage
                    (MEC).{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> 2D- </span> Use code 2D, if
                  the employee was in a Limited Non-Assessment Period or an
                  initial measurement period. Don’t use code 2D if the
                  multiemployer interim relief rule is applied to the employee,
                  instead use code 2E.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> 2E- </span> Use code 2E,
                  For any month when the employee is subject to the
                  multiemployer arrangement interim guidance.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> 2F- </span> Use code 2F, if
                  the ALE Member determines their employee&apos;s affordability
                  using the section 4980H Form W-2 safe harbor under section
                  4980H(b). It must be used every month of the year when the
                  employee is eligible for health Coverage.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> 2G- </span> Use code 2G, if
                  the ALE Member used the section 4980H federal poverty line
                  safe harbor to determine their employee&apos;s affordability
                  for any month.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> 2H- </span> Use code 2H, if
                  the ALE Member used the section 4980H rate of pay safe harbor
                  to determine their employee&apos;s affordability for any
                  month.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> 2I- </span> Reserved
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 17: </span> Please
                  enter the Zip code that the employer used to calculate the
                  employee&apos;s affordability. This field is required only if
                  the employee was offered an individual coverage Health
                  Reimbursement Arrangement(HRA). This is required only when the
                  code 1L, 1M, 1N, 1O, 1P, 1Q, 1T, or 1U in line 15
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mt: 2, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> Do not enter any value if the mortgage is
                  Acquired and enter the date of acquisition on Box 11.
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Part III - Covered Individuals
                </Typography>
                <Box sx={{ border: '1px solid #333', mb: 1 }}>
                  <picture>
                    <source srcSet="/assets/images/individuals-1095-c-img.webp" />
                    <source srcSet="/assets/images/individuals-1095-c-img.png" />
                    <img
                      src="/assets/images/individuals-1095-c-img.png"
                      loading="lazy"
                      alt="Part III - Covered Individuals "
                      width={760}
                      height={538}
                      className="proportional-img"
                    />
                  </picture>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 18 through 30: </span>{' '}
                  Enter the details of the covered individual along with the
                  employee
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Column (a) – </span> Enter
                  the legal full name of the health coverage offered employee.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Column (b) – </span> Enter
                  employee SSN (Social Security Number) or TIN ( Taxpayer
                  Identification Number)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Column (c) – </span> Enter
                  the employee&apos;s date of birth if SSN or TIN is not
                  available.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Column (d) – </span> Check
                  this box if the employee was covered every month of the year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Column (e) – </span> Check
                  the specific months the individual employee was covered, if
                  the coverage offered was not for all 12 months.
                </Typography>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided is based on guidelines from the
                        IRS website. For more information, check the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i109495c.pdf"
                          sx={{ color: '#0202ff' }}
                          target="_blank"
                        >
                          IRS instructions for form 1095-C
                        </Link>
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Feeling Puzzled while filing Form 1095-C/1094-C?
            </Typography>
            <Typography
              sx={{
                fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                fontWeight: 500,
                color: '#333333',
                mb: 2,
              }}
            >
              File Your ACA Forms Now with TaxZerone - It&apos;s Easy & Fast!
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 2, fontSize: { lg: 18, md: 18, sm: 16, xs: 16 } }}
            >
              Filing starts at <b>$ 2.49/Per</b> form and can go down up to
              $0.59/Per form
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1095cinstructions`}
              >
                File 1095-C/1094-C Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1095CInstructions;
